import React, { useState } from 'react';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { DraggableProvided } from 'react-beautiful-dnd';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import './AttachmentWidget.pcss';
import classnames from 'classnames';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { useAttachmentWidgetHandlers } from '../../helpers/widget-helpers/use-attachment-widget-handlers';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { acceptInputAttachmentsList, ATTACHMENT_EXTENSIONS } from './constants';
import { FileExtensionIcon } from '../../components/FileExtensionIcon/FileExtensionIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import Spinner from '../../components/Spinner';
import { PlatformType } from '@mm/state/model/CampaignsModel';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

export const useUploadFile = (widget: WidgetModel) => {
  const attachmentWidgetHandlers = useAttachmentWidgetHandlers();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (file: File | undefined, rejectedFiles: File[]) => {
    if (file) {
      try {
        setIsUploading(true);
        await attachmentWidgetHandlers.addFileToWidget(widget, file);
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
      }
    }

    if (rejectedFiles.length) {
      dispatch(
        modalActions.openPrettyErrorModal({
          title: 'Unsupported Extension',
          errorMessage: `Supported file extensions are ${ATTACHMENT_EXTENSIONS.join(
            ', ',
          )}`,
        }),
      );
    }
  };

  return { isUploading, uploadFile };
};

export const AttachmentWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    isSmsRelated,
    smsOnly,
    forInstagram,
    platforms,
  } = props;
  const { title, assetId } = widget.config;
  const { isUploading, uploadFile } = useUploadFile(widget);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept: ATTACHMENT_EXTENSIONS,
      onDrop: (files: File[], rejectedFiles: File[]) =>
        uploadFile(files[0], rejectedFiles),
    });

  return (
    <div
      className={classnames('attachment-widget', {
        'attachment-widget--has-file': assetId,
        'attachment-widget--active-drag': isDragActive,
        'attachment-widget--rejected-drag': isDragReject,
      })}
    >
      <FacebookPageIcon />

      <div className="attachment-widget__drop-area" {...getRootProps()}>
        {isUploading && (
          <div className="attachment-widget__uploading-wrapper">
            <Spinner
              show={true}
              position="absolute"
              backgroundColor="none"
              size={30}
            />
          </div>
        )}

        <input {...getInputProps()} accept={acceptInputAttachmentsList} />

        {assetId ? (
          <div className="attachment-widget__file-preview-wrapper">
            <FileExtensionIcon />
            <div className="attachment-widget__uploaded-file-name">{title}</div>
          </div>
        ) : (
          <div className="attachment-widget__no-file-wrapper">
            <div className="attachment-widget__supported-files-wrapper">
              {ATTACHMENT_EXTENSIONS.map(extension => (
                <FileExtensionIcon key={extension}>
                  {extension}
                </FileExtensionIcon>
              ))}
            </div>
          </div>
        )}

        <div className="attachment-widget__no-file-wrapper-bottom">
          <div className="attachment-widget__drop-area-text">
            Drag and drop or
            <span className="attachment-widget__choose-file">choose file</span>
          </div>
        </div>
      </div>

      <WidgetControls
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />

      {isSmsRelated && !widget.supportedByPlatforms.sms && (
        <ErrorPortal widget={widget}>
          <WidgetUnsupportedError
            className="attachment-widget__warning"
            isError={smsOnly}
            type="sms"
          />
        </ErrorPortal>
      )}
      {forInstagram && !widget.supportedByPlatforms.instagram && (
        <ErrorPortal widget={widget}>
          <WidgetUnsupportedError
            className="attachment-widget__warning"
            isError
            type="instagram"
          />
        </ErrorPortal>
      )}
    </div>
  );
};
